export const M5_UNIT_9 = {
  id: "m5-u9-stories",
  title: "Unit 9: Telling Stories & Giving Reasons",
  topic: "Describing movies, books, events, and explaining why you liked them",
  iconType: "book-open",
  grammarImage: "/grammar/grammar-m5-u9.jpg",
  grammarText: "This unit teaches you how to tell stories and give opinions about movies, books, shows, and events. You'll learn to use 'about' to describe what something is about (It's a movie about space), sequence words to organize your story (First, Then, After that, Finally), and 'because' to explain your opinions (I loved it because it was funny). This combines past simple verbs you already know (watched, went, saw) with new vocabulary for entertainment (director, author, drama, comedy) and opinion adjectives (thrilling, boring, romantic). These patterns let you write reviews, describe your weekend, and explain your tastes—essential for social conversations and written communication.",

  sets: [
    // ===================================================================
    // SET 1: DISCOVERING "ABOUT" (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u9-s1",
      title: "Set 1: What's It About?",
      questions: [
        {
          type: "match",
          q: "Match entertainment vocabulary:",
          pairs: [
            { left: "Movie/Film", right: "Watch in cinema/theater" },
            { left: "Book", right: "Read" },
            { left: "Show/Series", right: "Watch on TV/Netflix" }
          ],
          a: "completed",
          tip: "Movie (US) = Film (UK)!",
          successtip: "✓ Three types of entertainment! Movie 🎬, Book 📖, Show 📺"
        },
        {
          type: "match",
          q: "Match genres (types of stories):",
          pairs: [
            { left: "Comedy", right: "Funny, makes you laugh" },
            { left: "Drama", right: "Serious, emotional" },
            { left: "Action", right: "Fighting, chasing, explosions" }
          ],
          a: "completed",
          tip: "Genre = tipo/género! Comedy = comedia, Drama = drama!",
          successtip: "✓ Three main genres! Comedy 😂, Drama 😢, Action 💥"
        },
        {
          type: "mc",
          q: "PATTERN: To describe what a story is about, use: 'It's a movie ___ robots.'",
          options: ["about", "of", "with"],
          a: "about",
          tip: "'About' introduces the topic/subject!",
          successtip: "✓ ABOUT! 'It's about...' = Trata de... / Es sobre... 🎬"
        },
        {
          type: "input",
          q: "COMPLETE: 'The Hunger Games is a movie ___ a girl who fights in a competition.' (topic word)",
          a: "about",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ ABOUT! Describes the subject/topic! 🏹"
        },
        {
          type: "order",
          q: "Traduce: Es un libro sobre un detective en Londres.",
          words: ["It's", "a", "book", "about", "a", "detective", "in", "London"],
          a: "It's a book about a detective in London",
          successtip: "✓ Book ABOUT! Pattern established! 📖🔍"
        },
        {
          type: "mc",
          q: "NETFLIX: 'Stranger Things is a show ___ kids in the 1980s who fight monsters.'",
          options: ["about", "with", "from"],
          a: "about",
          successtip: "✓ ABOUT! Stranger Things context! Relatable! 👾"
        },
        {
          type: "input",
          q: "Harry Potter is a movie series ___ a young wizard. (topic word)",
          a: "about",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ ABOUT! Harry Potter = universal reference! ⚡🧙"
        },
        {
          type: "match",
          q: "Match more genres:",
          pairs: [
            { left: "Horror", right: "Scary, frightening" },
            { left: "Romance", right: "Love story" },
            { left: "Thriller", right: "Suspenseful, tense" }
          ],
          a: "completed",
          tip: "Horror = terror, Romance = romance, Thriller = suspense!",
          successtip: "✓ Three more genres! Horror 😱, Romance 💕, Thriller 😰"
        },
        {
          type: "mc",
          q: "IDENTIFY GENRE: 'It's a movie about two people who fall in love on a ship.' What genre?",
          options: ["Romance", "Action", "Comedy"],
          a: "Romance",
          tip: "Fall in love = romance!",
          successtip: "✓ ROMANCE! (Titanic reference!) Love story! 🚢💕"
        },
        {
          type: "order",
          q: "Traduce: Es una serie sobre médicos en un hospital.",
          words: ["It's", "a", "show", "about", "doctors", "in", "a", "hospital"],
          a: "It's a show about doctors in a hospital",
          tip: "Show = serie/programa! Doctors = médicos!",
          successtip: "✓ Show ABOUT! (Grey's Anatomy, House, ER vibes!) 🏥📺"
        }
      ]
    },

    // ===================================================================
    // SET 2: ENTERTAINMENT VOCABULARY (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u9-s2",
      title: "Set 2: Movie & Book Vocabulary",
      questions: [
        {
          type: "match",
          q: "Match people who create entertainment:",
          pairs: [
            { left: "Director", right: "Makes/directs movies" },
            { left: "Author/Writer", right: "Writes books/stories" },
            { left: "Actor", right: "Performs in movies/plays" }
          ],
          a: "completed",
          tip: "Director = director, Author = autor, Actor = actor!",
          successtip: "✓ Three key creators! Director 🎬, Author ✍️, Actor 🎭"
        },
        {
          type: "mc",
          q: "MOVIE ROLE: 'Steven Spielberg ___ Jurassic Park.' (Who makes a movie?)",
          options: ["directed", "wrote", "acted in"],
          a: "directed",
          tip: "Director directs! Spielberg = famous director!",
          successtip: "✓ DIRECTED! Director → directs/directed! 🎬🦖"
        },
        {
          type: "input",
          q: "BOOK: 'J.K. Rowling ___ the Harry Potter books.' (create verb for books)",
          a: "wrote",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["written", "writes"] },
          tip: "Write→wrote (irregular)! Author writes!",
          successtip: "✓ WROTE! Author → writes books! Write→wrote! ✍️📚"
        },
        {
          type: "order",
          q: "Traduce: Leonardo DiCaprio actuó en Titanic.",
          words: ["Leonardo", "DiCaprio", "acted", "in", "Titanic"],
          a: "Leonardo DiCaprio acted in Titanic",
          tip: "Act→acted (regular)! Actor acts/acted!",
          successtip: "✓ ACTED! Actor → acts in movies! 🎭🎬"
        },
        {
          type: "match",
          q: "Match story elements:",
          pairs: [
            { left: "Plot", right: "The story/what happens" },
            { left: "Character", right: "A person in the story" },
            { left: "Ending", right: "How the story finishes" }
          ],
          a: "completed",
          tip: "Plot = trama, Character = personaje, Ending = final!",
          successtip: "✓ Story elements! Plot 📖, Character 👤, Ending 🏁"
        },
        {
          type: "mc",
          q: "STRUCTURE: 'The ___ was confusing. I didn't understand what happened!'",
          options: ["plot", "actor", "director"],
          a: "plot",
          tip: "Plot = the story itself!",
          successtip: "✓ PLOT! The storyline/what happens! 📖❓"
        },
        {
          type: "input",
          q: "CHARACTER: 'My favorite ___ in Harry Potter is Hermione. She's smart and brave!'",
          a: "character",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ CHARACTER! Person in the story! Hermione! 📚👧"
        },
        {
          type: "match",
          q: "Match hero/villain vocabulary:",
          pairs: [
            { left: "Hero", right: "Good guy/protagonist" },
            { left: "Villain", right: "Bad guy/antagonist" },
            { left: "Main character", right: "Most important person" }
          ],
          a: "completed",
          tip: "Hero = héroe, Villain = villano, Main = principal!",
          successtip: "✓ Key characters! Hero 🦸, Villain 🦹, Main character ⭐"
        },
        {
          type: "mc",
          q: "IDENTIFY: 'Darth Vader is the ___ in Star Wars.'",
          options: ["villain", "hero", "director"],
          a: "villain",
          tip: "Darth Vader = bad guy!",
          successtip: "✓ VILLAIN! The bad guy! Darth Vader! 🦹‍♂️⚫"
        },
        {
          type: "order",
          q: "Traduce: El héroe luchó contra el villano y ganó.",
          words: ["The", "hero", "fought", "the", "villain", "and", "won"],
          a: "The hero fought the villain and won",
          tip: "Fight→fought, win→won! Both irregular!",
          successtip: "✓ Classic story! Hero beats villain! Fight→fought, win→won! 🦸💥🦹"
        }
      ]
    },

    // ===================================================================
    // SET 3: SEQUENCE WORDS - FIRST, THEN (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u9-s3",
      title: "Set 3: Telling Stories in Order",
      questions: [
        {
          type: "match",
          q: "Match sequence words to their position:",
          pairs: [
            { left: "First", right: "Beginning (1st event)" },
            { left: "Then", right: "Next event (2nd, 3rd...)" },
            { left: "Finally", right: "Last event (conclusion)" }
          ],
          a: "completed",
          tip: "First = primero, Then = luego/entonces, Finally = finalmente!",
          successtip: "✓ Sequence words organize stories! First ➡️ Then ➡️ Finally 📖"
        },
        {
          type: "mc",
          q: "STORY START: '___, I woke up at 7am. Then I had breakfast.'",
          options: ["First", "Finally", "Then"],
          a: "First",
          tip: "First = always beginning!",
          successtip: "✓ FIRST! Always starts the sequence! 🥇"
        },
        {
          type: "input",
          q: "MIDDLE EVENT: 'First I went to the store. ___, I bought some food.' (next event)",
          a: "Then",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["After that", "Next"] },
          tip: "Then = después/luego! Most common middle word!",
          successtip: "✓ THEN! Connects events! First → Then → ... ➡️"
        },
        {
          type: "order",
          q: "ORDER: Put in sequence: breakfast / woke up / went to work",
          words: ["First", "I", "woke", "up.", "Then", "I", "ate", "breakfast.", "Finally", "I", "went", "to", "work"],
          a: "First I woke up. Then I ate breakfast. Finally I went to work",
          tip: "Logical order! Wake → eat → go!",
          successtip: "✓ Perfect sequence! First → Then → Finally! Morning routine! ☀️🍳💼"
        },
        {
          type: "mc",
          q: "END: 'First we arrived. Then we checked in. ___, we went to our room.'",
          options: ["Finally", "First", "Then"],
          a: "Finally",
          tip: "Finally = last step!",
          successtip: "✓ FINALLY! Ends the sequence! Hotel check-in story! 🏨"
        },
        {
          type: "input",
          q: "MOVIE PLOT: 'First the hero found a clue. ___, he chased the criminal.' (next word)",
          a: "Then",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Next", "After that"] },
          successtip: "✓ THEN! Detective story sequence! 🔍➡️🏃"
        },
        {
          type: "match",
          q: "More sequence words (synonyms):",
          pairs: [
            { left: "Then = Next", right: "Both mean siguiente" },
            { left: "After that", right: "Después de eso" },
            { left: "Finally = At last", right: "Both mean finalmente" }
          ],
          a: "completed",
          tip: "Multiple options! All work the same way!",
          successtip: "✓ Synonyms! Then/Next, After that, Finally/At last! 🔄"
        },
        {
          type: "mc",
          q: "WEEKEND STORY: 'First I slept late. Then I had brunch. ___ I went shopping. Finally I watched a movie.' (middle event)",
          options: ["After that", "First", "Finally"],
          a: "After that",
          tip: "'After that' = después de eso! Another middle connector!",
          successtip: "✓ AFTER THAT! Another middle connector! Weekend story! 😴🥞🛍️🎬"
        },
        {
          type: "input",
          q: "COMPLETE: 'First the villain stole the money. Then the hero chased him. ___, the hero caught the villain!' (last event)",
          a: "Finally",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["At last"] },
          successtip: "✓ FINALLY! Happy ending! Hero wins! 🦸✅🦹❌"
        },
        {
          type: "order",
          q: "Traduce: Primero leí el libro. Luego vi la película. Finalmente escribí una reseña.",
          words: ["First", "I", "read", "the", "book.", "Then", "I", "watched", "the", "movie.", "Finally", "I", "wrote", "a", "review"],
          a: "First I read the book. Then I watched the movie. Finally I wrote a review",
          tip: "Read→read (same!), watch→watched, write→wrote!",
          successtip: "✓ Complete sequence! Book → movie → review! 📖🎬✍️"
        }
      ]
    },

    // ===================================================================
    // SET 4: PRACTICE STORYTELLING WITH SEQUENCE (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u9-s4",
      title: "Set 4: My Day, My Weekend",
      questions: [
        {
          type: "match",
          q: "Match common daily activities:",
          pairs: [
            { left: "Morning", right: "Woke up, had breakfast" },
            { left: "Afternoon", right: "Had lunch, worked/studied" },
            { left: "Evening", right: "Had dinner, relaxed" }
          ],
          a: "completed",
          successtip: "✓ Daily timeline! Morning ☀️, Afternoon 🌤️, Evening 🌙"
        },
        {
          type: "mc",
          q: "COMPLETE: 'Yesterday was great! ___, I woke up at 10am. Then I had a big breakfast. Finally I went to the park.'",
          options: ["First", "Finally", "Then"],
          a: "First",
          successtip: "✓ FIRST! Story opener! Relaxed day! 😊"
        },
        {
          type: "input",
          q: "MY WEEKEND: 'On Saturday, first I cleaned my room. ___, I did laundry. Finally I watched Netflix.' (middle)",
          a: "Then",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Next", "After that"] },
          successtip: "✓ THEN! Productive Saturday! Chores then reward! 🧹📺"
        },
        {
          type: "order",
          q: "ORDER: Party sequence: arrived / danced / went home",
          words: ["First", "I", "arrived", "at", "the", "party.", "Then", "I", "danced", "all", "night.", "Finally", "I", "went", "home"],
          a: "First I arrived at the party. Then I danced all night. Finally I went home",
          successtip: "✓ Party night! Arrive → dance → home! 🎉💃🏠"
        },
        {
          type: "mc",
          q: "RESTAURANT: 'First we looked at the menu. ___ we ordered food. Finally the waiter brought our meal.'",
          options: ["Then", "First", "Finally"],
          a: "Then",
          successtip: "✓ THEN! Restaurant sequence! Menu → order → food! 🍽️"
        },
        {
          type: "input",
          q: "CONCERT: 'First the opening band played. Then the main band came on stage. ___, everyone went crazy!' (last)",
          a: "Finally",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["At last"] },
          successtip: "✓ FINALLY! Concert climax! Opening → main → chaos! 🎸🎤🎉"
        },
        {
          type: "match",
          q: "Match event types to sequences:",
          pairs: [
            { left: "Movie night", right: "Bought ticket → watched → left" },
            { left: "Shopping", right: "Entered store → browsed → bought" },
            { left: "Coffee date", right: "Met → talked → said goodbye" }
          ],
          a: "completed",
          successtip: "✓ Different event types! Each has natural sequence! 🎬🛍️☕"
        },
        {
          type: "mc",
          q: "VACATION START: '___ we packed our bags. Then we drove to the airport. Finally we boarded the plane!'",
          options: ["First", "Then", "Finally"],
          a: "First",
          successtip: "✓ FIRST! Vacation beginning! Pack → drive → board! ✈️"
        },
        {
          type: "input",
          q: "STUDY SESSION: 'First I reviewed my notes. ___ I did practice exercises. Finally I took the test.' (middle)",
          a: "Then",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["After that", "Next"] },
          successtip: "✓ THEN! Study progression! Review → practice → test! 📚✍️"
        },
        {
          type: "order",
          q: "Traduce: Primero cociné la cena. Luego comí con mi familia. Finalmente lavé los platos.",
          words: ["First", "I", "cooked", "dinner.", "Then", "I", "ate", "with", "my", "family.", "Finally", "I", "washed", "the", "dishes"],
          a: "First I cooked dinner. Then I ate with my family. Finally I washed the dishes",
          tip: "Cook→cooked, eat→ate (irreg!), wash→washed!",
          successtip: "✓ Dinner routine! Cook → eat → clean! Perfect sequence! 🍳🍽️🧼"
        }
      ]
    },

    // ===================================================================
    // SET 5: OPINIONS WITH "BECAUSE" (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u9-s5",
      title: "Set 5: I Loved It Because...",
      questions: [
        {
          type: "match",
          q: "Match opinion verbs (positive → negative):",
          pairs: [
            { left: "Loved", right: "Hated" },
            { left: "Enjoyed", right: "Didn't enjoy" },
            { left: "Liked", right: "Didn't like" }
          ],
          a: "completed",
          tip: "Love = amar/encantar, Enjoy = disfrutar, Like = gustar!",
          successtip: "✓ Opinion spectrum! Loved 😍 → Liked 👍 → Didn't like 👎 → Hated 😤"
        },
        {
          type: "match",
          q: "Match positive opinion adjectives:",
          pairs: [
            { left: "Funny", right: "Makes you laugh" },
            { left: "Exciting", right: "Keeps you on edge" },
            { left: "Amazing", right: "Incredibly good" }
          ],
          a: "completed",
          tip: "Funny = gracioso, Exciting = emocionante, Amazing = increíble!",
          successtip: "✓ Positive adjectives! Funny 😂, Exciting 😃, Amazing 🤩"
        },
        {
          type: "mc",
          q: "PATTERN: 'I loved the movie ___ it was funny.' (reason connector)",
          options: ["because", "but", "and"],
          a: "because",
          tip: "Because = porque! Gives reason/explanation!",
          successtip: "✓ BECAUSE! Opinion + because + reason! 💕➡️😂"
        },
        {
          type: "input",
          q: "COMPLETE: 'I enjoyed the book ___ the characters were interesting.' (reason word)",
          a: "because",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ BECAUSE! Enjoyed (opinion) + because (reason)! 📖✅"
        },
        {
          type: "order",
          q: "Traduce: Me encantó la película porque fue emocionante.",
          words: ["I", "loved", "the", "movie", "because", "it", "was", "exciting"],
          a: "I loved the movie because it was exciting",
          successtip: "✓ Opinion + because + adjective! Pattern! 🎬❤️⚡"
        },
        {
          type: "match",
          q: "Match negative opinion adjectives:",
          pairs: [
            { left: "Boring", right: "Not interesting/dull" },
            { left: "Slow", right: "Nothing happens fast" },
            { left: "Confusing", right: "Hard to understand" }
          ],
          a: "completed",
          tip: "Boring = aburrido, Slow = lento, Confusing = confuso!",
          successtip: "✓ Negative adjectives! Boring 😴, Slow 🐌, Confusing 🤔"
        },
        {
          type: "mc",
          q: "NEGATIVE OPINION: 'I didn't like the show because it was ___.'",
          options: ["boring", "exciting", "funny"],
          a: "boring",
          tip: "Negative opinion needs negative adjective!",
          successtip: "✓ BORING! Didn't like + because + boring! Negative combo! 😴"
        },
        {
          type: "input",
          q: "HONEST REVIEW: 'I hated the book because the plot was ___.' (negative adjective for not interesting)",
          a: "boring",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["confusing", "slow", "bad", "terrible"] },
          successtip: "✓ Negative adjective! Hated + because + boring/confusing! 📖❌"
        },
        {
          type: "mc",
          q: "COMPLETE: 'I ___ the concert because the band was amazing!'",
          options: ["loved", "hated", "didn't like"],
          a: "loved",
          tip: "Amazing = positive! Need positive opinion!",
          successtip: "✓ LOVED! Positive opinion + because + amazing! 🎸❤️"
        },
        {
          type: "order",
          q: "Traduce: No disfruté la serie porque fue lenta y aburrida.",
          words: ["I", "didn't", "enjoy", "the", "show", "because", "it", "was", "slow", "and", "boring"],
          a: "I didn't enjoy the show because it was slow and boring",
          tip: "Can combine two adjectives with 'and'!",
          successtip: "✓ Double adjectives! Slow AND boring! Extra negative! 📺😴🐌"
        }
      ]
    },

    // ===================================================================
    // SET 6: MORE OPINION ADJECTIVES (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u9-s6",
      title: "Set 6: Describing What You Watched",
      questions: [
        {
          type: "match",
          q: "Match adjectives to feelings:",
          pairs: [
            { left: "Scary", right: "Made you afraid 😱" },
            { left: "Sad", right: "Made you cry 😢" },
            { left: "Romantic", right: "About love 💕" }
          ],
          a: "completed",
          tip: "Scary = de miedo, Sad = triste, Romantic = romántico!",
          successtip: "✓ Emotion adjectives! Scary 😱, Sad 😢, Romantic 💕"
        },
        {
          type: "mc",
          q: "HORROR: 'I watched a horror movie last night. I loved it because it was really ___!'",
          options: ["scary", "romantic", "funny"],
          a: "scary",
          tip: "Horror movie = scary!",
          successtip: "✓ SCARY! Horror = scary! Genre match! 👻😱"
        },
        {
          type: "input",
          q: "ROMANCE: 'I watched The Notebook. I cried at the end! It was so ___.' (emotional adjective)",
          a: "sad",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["romantic", "emotional", "touching"] },
          tip: "Cried = lloré! Sad/romantic/touching all work!",
          successtip: "✓ SAD/ROMANTIC! Crying = emotional! The Notebook! 😢💕"
        },
        {
          type: "order",
          q: "Traduce: Me gustó la comedia porque fue muy graciosa.",
          words: ["I", "liked", "the", "comedy", "because", "it", "was", "very", "funny"],
          a: "I liked the comedy because it was very funny",
          tip: "Can add 'very' before adjective for emphasis!",
          successtip: "✓ VERY funny! Very = muy! Emphasis! Comedy = funny! 😂"
        },
        {
          type: "match",
          q: "Match more positive adjectives:",
          pairs: [
            { left: "Thrilling", right: "Edge of your seat" },
            { left: "Interesting", right: "Kept your attention" },
            { left: "Brilliant", right: "Excellent/outstanding" }
          ],
          a: "completed",
          tip: "Thrilling = emocionante, Interesting = interesante, Brilliant = brillante!",
          successtip: "✓ Advanced positives! Thrilling 😃, Interesting 🤔, Brilliant 🌟"
        },
        {
          type: "mc",
          q: "THRILLER: 'I watched a mystery movie. I couldn't stop watching! It was so ___!'",
          options: ["thrilling", "boring", "slow"],
          a: "thrilling",
          tip: "Couldn't stop = engaged! Thrilling!",
          successtip: "✓ THRILLING! Suspenseful! Couldn't stop watching! 😰📺"
        },
        {
          type: "input",
          q: "DOCUMENTARY: 'I watched a documentary about space. I learned so much! It was really ___.' (positive for learning)",
          a: "interesting",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["educational", "fascinating", "brilliant"] },
          successtip: "✓ INTERESTING! Learning = interesting/educational! 🌌📚"
        },
        {
          type: "match",
          q: "Match more negative adjectives:",
          pairs: [
            { left: "Terrible", right: "Very bad" },
            { left: "Disappointing", right: "Not what you expected" },
            { left: "Weird", right: "Strange/odd" }
          ],
          a: "completed",
          tip: "Terrible = terrible, Disappointing = decepcionante, Weird = raro!",
          successtip: "✓ Strong negatives! Terrible 😡, Disappointing 😞, Weird 😕"
        },
        {
          type: "mc",
          q: "BAD MOVIE: 'I hated the movie. The acting was bad, the plot made no sense. It was ___!'",
          options: ["terrible", "brilliant", "amazing"],
          a: "terrible",
          successtip: "✓ TERRIBLE! Super negative! Multiple problems! 🎬❌😤"
        },
        {
          type: "order",
          q: "Traduce: No me gustó porque las actuaciones fueron decepcionantes.",
          words: ["I", "didn't", "like", "it", "because", "the", "acting", "was", "disappointing"],
          a: "I didn't like it because the acting was disappointing",
          tip: "Acting = las actuaciones! Disappointing = decepcionante!",
          successtip: "✓ DISAPPOINTING! Specific criticism! Expected better! 🎭😞"
        }
      ]
    },

    // ===================================================================
    // SET 7: COMPLETE REVIEWS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u9-s7",
      title: "Set 7: Writing Full Reviews",
      questions: [
        {
          type: "match",
          q: "Match review structure:",
          pairs: [
            { left: "What (title + about)", right: "It's a movie about..." },
            { left: "Opinion", right: "I loved/hated..." },
            { left: "Reason", right: "because it was..." }
          ],
          a: "completed",
          successtip: "✓ Review structure! What + Opinion + Reason! 📝"
        },
        {
          type: "mc",
          q: "COMPLETE REVIEW: 'Last night I watched Inception. ___ a movie about dreams. I loved it because it was brilliant!'",
          options: ["It's", "Its", "There"],
          a: "It's",
          tip: "It's = It is! Describes the movie!",
          successtip: "✓ IT'S! It's (It is) a movie about... 🎬✅"
        },
        {
          type: "input",
          q: "NETFLIX REVIEW: 'I just finished Stranger Things. It's a show ___ kids in the 1980s. I loved it because it was scary and exciting!'",
          a: "about",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ ABOUT! Complete review structure! Show about + opinion + reason! 📺👾"
        },
        {
          type: "order",
          q: "ORDER: Build a review for Harry Potter",
          words: ["It's", "a", "movie", "about", "a", "wizard.", "I", "loved", "it", "because", "it", "was", "magical"],
          a: "It's a movie about a wizard. I loved it because it was magical",
          successtip: "✓ Complete review! About + opinion + because! Harry Potter! ⚡🪄"
        },
        {
          type: "mc",
          q: "BOOK REVIEW: 'I read The Hunger Games. It's a book about a girl who fights in a competition. I ___ it because the plot was thrilling!'",
          options: ["enjoyed", "boring", "because"],
          a: "enjoyed",
          tip: "Need opinion verb before 'because'!",
          successtip: "✓ ENJOYED! Opinion verb fits! Thrilling = positive! 📖🏹"
        },
        {
          type: "input",
          q: "NEGATIVE REVIEW: 'I watched Twilight. It's a movie about vampires. I didn't like it ___ it was slow and boring.'",
          a: "because",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ BECAUSE! Negative review! Didn't like + because + slow/boring! 🧛❌"
        },
        {
          type: "match",
          q: "Match reviews to genres:",
          pairs: [
            { left: "Horror: scary, frightening", right: "😱" },
            { left: "Comedy: funny, hilarious", right: "😂" },
            { left: "Romance: romantic, sweet", right: "💕" }
          ],
          a: "completed",
          successtip: "✓ Genre-adjective match! Horror→scary, Comedy→funny, Romance→romantic! 🎬"
        },
        {
          type: "mc",
          q: "COMPLETE: 'I watched The Shawshank Redemption. It's a drama about prisoners. I ___ because it was emotional and inspiring!'",
          options: ["loved it", "hated it", "it's about"],
          a: "loved it",
          successtip: "✓ LOVED IT! Emotional + inspiring = positive opinion! 🎬😢💪"
        },
        {
          type: "input",
          q: "YOUR REVIEW: 'I watched a documentary about oceans. It's ___ marine life. I found it fascinating!' (topic word)",
          a: "about",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ ABOUT! Documentary structure! About + found it + adjective! 🌊📺"
        },
        {
          type: "order",
          q: "Traduce: Leí un libro sobre un detective. Me encantó porque el misterio fue intrigante.",
          words: ["I", "read", "a", "book", "about", "a", "detective.", "I", "loved", "it", "because", "the", "mystery", "was", "intriguing"],
          a: "I read a book about a detective. I loved it because the mystery was intriguing",
          tip: "Intriguing = intrigante/fascinante! Advanced adjective!",
          successtip: "✓ Complete review! Book about + loved + because + intriguing! 🔍📖"
        }
      ]
    },

    // ===================================================================
    // SET 8: EVENT STORIES (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u9-s8",
      title: "Set 8: Concerts, Parties, Events",
      questions: [
        {
          type: "match",
          q: "Match event types:",
          pairs: [
            { left: "Concert", right: "Live music performance" },
            { left: "Party", right: "Social celebration" },
            { left: "Play/Theater", right: "Live stage performance" }
          ],
          a: "completed",
          tip: "Concert = concierto, Party = fiesta, Play = obra de teatro!",
          successtip: "✓ Event types! Concert 🎸, Party 🎉, Theater 🎭"
        },
        {
          type: "mc",
          q: "CONCERT REVIEW: 'Last weekend I went to a concert. First I bought a ticket. ___ I found my seat. Finally the band started playing!'",
          options: ["Then", "Finally", "Because"],
          a: "Then",
          successtip: "✓ THEN! Concert sequence! Ticket → seat → music! 🎫🪑🎸"
        },
        {
          type: "input",
          q: "CONCERT OPINION: 'First the band played their new songs. Then they played the classics. ___ everyone sang along. I loved it because the energy was amazing!'",
          a: "Finally",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["At last"] },
          successtip: "✓ FINALLY! Complete concert story + opinion! Sequence + because! 🎤🎶😍"
        },
        {
          type: "order",
          q: "ORDER: Party story: arrived / danced / left",
          words: ["First", "I", "arrived", "at", "9pm.", "Then", "I", "danced", "all", "night.", "Finally", "I", "left", "at", "2am.", "I", "loved", "it", "because", "it", "was", "fun"],
          a: "First I arrived at 9pm. Then I danced all night. Finally I left at 2am. I loved it because it was fun",
          successtip: "✓ Complete party story! Sequence + times + opinion! 🎉💃🕺"
        },
        {
          type: "mc",
          q: "THEATER: 'I went to see a play about Romeo and Juliet. I ___ because the actors were brilliant!'",
          options: ["enjoyed it", "it was", "because"],
          a: "enjoyed it",
          tip: "Need opinion before 'because'!",
          successtip: "✓ ENJOYED IT! Theater review! Actors = brilliant! 🎭👏"
        },
        {
          type: "input",
          q: "WEDDING: 'I went to my friend's wedding. First there was a ceremony. ___ we had dinner. Finally we danced. It was beautiful!' (middle)",
          a: "Then",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["After that", "Next"] },
          successtip: "✓ THEN! Wedding sequence! Ceremony → dinner → dance! 💒🍽️💃"
        },
        {
          type: "match",
          q: "Match event adjectives:",
          pairs: [
            { left: "Crowded", right: "Lots of people" },
            { left: "Loud", right: "Very noisy" },
            { left: "Lively", right: "Full of energy" }
          ],
          a: "completed",
          tip: "Crowded = lleno, Loud = ruidoso, Lively = animado!",
          successtip: "✓ Event descriptors! Crowded 👥, Loud 🔊, Lively ⚡"
        },
        {
          type: "mc",
          q: "FESTIVAL: 'I went to a music festival. There were 50,000 people! It was so ___!'",
          options: ["crowded", "quiet", "small"],
          a: "crowded",
          successtip: "✓ CROWDED! 50,000 people = very crowded! 🎪👥"
        },
        {
          type: "input",
          q: "BIRTHDAY: 'I went to a birthday party. First we ate cake. Then we played games. ___ we sang happy birthday. I enjoyed it because it was lively!' (last)",
          a: "Finally",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["At last"] },
          successtip: "✓ FINALLY! Complete party! Sequence + opinion + adjective! 🎂🎮🎵"
        },
        {
          type: "order",
          q: "Traduce: Fui a un concierto. Primero el grupo telonero tocó. Luego la banda principal salió. Finalmente todos saltamos y cantamos. Fue increíble.",
          words: ["I", "went", "to", "a", "concert.", "First", "the", "opening", "band", "played.", "Then", "the", "main", "band", "came", "out.", "Finally", "everyone", "jumped", "and", "sang.", "It", "was", "amazing"],
          a: "I went to a concert. First the opening band played. Then the main band came out. Finally everyone jumped and sang. It was amazing",
          successtip: "✓ Epic concert story! Multi-sentence + sequence + opinion! 🎸🎤🎉"
        }
      ]
    },

    // ===================================================================
    // SET 9: COMBINING EVERYTHING (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u9-s9",
      title: "Set 9: Complex Stories",
      questions: [
        {
          type: "match",
          q: "Match story types to structures:",
          pairs: [
            { left: "Movie review", right: "About + opinion + because" },
            { left: "Weekend story", right: "First + then + finally" },
            { left: "Event narrative", right: "Sequence + opinion" }
          ],
          a: "completed",
          successtip: "✓ Different structures for different stories! 🎬📖🎉"
        },
        {
          type: "mc",
          q: "COMPLEX REVIEW: 'Yesterday I watched Avengers. It's a movie ___ superheroes who save the world. First they fought individually. Then they worked together. Finally they won! I loved it because it was exciting!'",
          options: ["about", "because", "then"],
          a: "about",
          tip: "About introduces the topic!",
          successtip: "✓ ABOUT! Complex review: about + sequence + opinion! 🦸‍♂️💥"
        },
        {
          type: "input",
          q: "WEEKEND VLOG: 'Last weekend was awesome! On Saturday, first I slept late. ___ I had brunch with friends. After that we went shopping. Finally we watched a movie. I loved it because I needed to relax!' (first middle word)",
          a: "Then",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Next"] },
          successtip: "✓ THEN! Multi-event weekend! Multiple sequences + opinion! 😴🥞🛍️🎬"
        },
        {
          type: "order",
          q: "ORDER: Complete story about reading a book",
          words: ["I", "read", "Pride", "and", "Prejudice.", "It's", "a", "book", "about", "love", "in", "England.", "First", "Elizabeth", "met", "Mr.", "Darcy.", "Then", "they", "argued.", "Finally", "they", "fell", "in", "love.", "I", "loved", "it", "because", "it", "was", "romantic"],
          a: "I read Pride and Prejudice. It's a book about love in England. First Elizabeth met Mr. Darcy. Then they argued. Finally they fell in love. I loved it because it was romantic",
          successtip: "✓ Complete narrative! About + plot sequence + opinion! Classic literature! 📖💕"
        },
        {
          type: "mc",
          q: "COMPLETE: 'I watched a horror movie about ghosts in a house. ___ a family moved in. Then strange things started happening. Finally they discovered the truth. I loved it because it was really scary!'",
          options: ["First", "Finally", "Because"],
          a: "First",
          successtip: "✓ FIRST! Horror plot + sequence! About + First/Then/Finally + opinion! 👻🏚️"
        },
        {
          type: "input",
          q: "RESTAURANT REVIEW: 'Last night I went to a new Italian restaurant. First we looked at the menu. Then we ordered pasta. ___ the waiter brought dessert. I enjoyed it because the food was delicious!' (last event)",
          a: "Finally",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["At last", "After that"] },
          successtip: "✓ FINALLY! Restaurant experience! Menu → main → dessert + opinion! 🍝🍰😋"
        },
        {
          type: "match",
          q: "Match advanced opinion phrases:",
          pairs: [
            { left: "I absolutely loved", right: "Super positive" },
            { left: "I kind of liked", right: "Mildly positive" },
            { left: "I really hated", right: "Super negative" }
          ],
          a: "completed",
          tip: "Absolutely/really = emphasis! Kind of = un poco/más o menos!",
          successtip: "✓ Intensity! Absolutely loved 😍, kind of liked 😊, really hated 😤"
        },
        {
          type: "mc",
          q: "EMPHASIS: 'I watched Parasite. It's a thriller about class differences. I ___ because the plot was brilliant and the ending shocked me!'",
          options: ["absolutely loved it", "kind of liked it", "didn't watch it"],
          a: "absolutely loved it",
          tip: "Brilliant + shocked = super positive!",
          successtip: "✓ ABSOLUTELY LOVED IT! Strong emphasis! Brilliant + shocking! 🎬🤯❤️"
        },
        {
          type: "input",
          q: "MIXED OPINION: 'I watched a comedy about office workers. The beginning was great, but then it got boring. I ___ of liked it.' (mild positive phrase)",
          a: "kind",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["kinda", "sort"] },
          tip: "'Kind of' = más o menos! Mixed feelings!",
          successtip: "✓ KIND OF! Mixed opinion! Not strong! More nuanced! 😐"
        },
        {
          type: "order",
          q: "FULL STORY: Traduce: Vi una película sobre un astronauta. Primero quedó atrapado en Marte. Luego cultivó papas. Finalmente fue rescatado. La amé absolutamente porque fue inspiradora.",
          words: ["I", "watched", "a", "movie", "about", "an", "astronaut.", "First", "he", "got", "stuck", "on", "Mars.", "Then", "he", "grew", "potatoes.", "Finally", "he", "was", "rescued.", "I", "absolutely", "loved", "it", "because", "it", "was", "inspiring"],
          a: "I watched a movie about an astronaut. First he got stuck on Mars. Then he grew potatoes. Finally he was rescued. I absolutely loved it because it was inspiring",
          tip: "The Martian! Get→got, grow→grew!",
          successtip: "✓ 🎉 COMPLETE STORY! About + First/Then/Finally + absolutely loved + because! The Martian! 🚀🥔❤️"
        },
        {
          type: "input",
          q: "YOUR TURN: Write 'about' + your favorite movie genre (action/comedy/horror/romance)",
          a: "about",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["any response"] },
          tip: "No wrong answer! Practice thinking in English!",
          successtip: "✓ Great! You're thinking about your own stories! Personal connection! 🎬❤️"
        }
      ]
    },

    // ===================================================================
    // SET 10: WRITING YOUR OWN REVIEWS (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u9-s10",
      title: "Set 10: Create Your Own Stories",
      questions: [
        {
          type: "match",
          q: "Review complete structure:",
          pairs: [
            { left: "Step 1: Title + about", right: "What is it about?" },
            { left: "Step 2: Plot (optional)", right: "First, Then, Finally" },
            { left: "Step 3: Opinion + reason", right: "I loved it because..." }
          ],
          a: "completed",
          successtip: "✓ Complete review structure! 3 steps! 📝✅"
        },
        {
          type: "mc",
          q: "START YOUR REVIEW: 'I watched Black Panther. ___'",
          options: ["It's a movie about a superhero king in Africa", "I loved it", "Because it was great"],
          a: "It's a movie about a superhero king in Africa",
          tip: "Always start with 'about' description!",
          successtip: "✓ Start with ABOUT! Describe first, opinion later! 🎬👑"
        },
        {
          type: "input",
          q: "ADD SEQUENCE: 'It's a movie about a king. ___ he returned to his country. Then he fought for the throne. Finally he became the leader.' (first word)",
          a: "First",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ FIRST! Add plot details with sequence! Makes review longer! 🎬⏰"
        },
        {
          type: "order",
          q: "FINISH REVIEW: Add opinion + reason",
          words: ["I", "absolutely", "loved", "it", "because", "it", "was", "inspiring", "and", "the", "visuals", "were", "stunning"],
          a: "I absolutely loved it because it was inspiring and the visuals were stunning",
          tip: "Can have multiple reasons with 'and'!",
          successtip: "✓ Complete ending! Opinion + because + multiple reasons! 🎬❤️✨"
        },
        {
          type: "mc",
          q: "BOOK REVIEW START: 'I read The Great Gatsby. ___'",
          options: ["It's a book about the American Dream in the 1920s", "I liked it", "It was interesting"],
          a: "It's a book about the American Dream in the 1920s",
          successtip: "✓ ABOUT! Always start with topic! Classic literature! 📖✨"
        },
        {
          type: "input",
          q: "TV SERIES: 'I binged Breaking Bad. It's a show ___ a chemistry teacher who becomes a criminal. I absolutely loved it because the characters were complex!' (topic word)",
          a: "about",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Binge = watch many episodes in a row! Modern verb!",
          successtip: "✓ ABOUT! Binge-watching review! Breaking Bad! 📺🧪"
        },
        {
          type: "match",
          q: "Match review platforms to content:",
          pairs: [
            { left: "Netflix review", right: "Shows/movies" },
            { left: "Goodreads review", right: "Books" },
            { left: "Yelp review", right: "Restaurants/events" }
          ],
          a: "completed",
          successtip: "✓ Different platforms! Same structure! 📱💬"
        },
        {
          type: "mc",
          q: "RESTAURANT REVIEW: 'I went to Olive Garden. It's a restaurant that serves Italian food. First I ordered soup. ___ I had pasta. Finally I got dessert. I enjoyed it because everything was delicious!'",
          options: ["Then", "Because", "About"],
          a: "Then",
          successtip: "✓ THEN! Restaurant reviews use sequence too! 🍝😋"
        },
        {
          type: "input",
          q: "EVENT REVIEW: 'I went to Comic-Con. First I saw the cosplayers. Then I met some actors. ___ I bought merchandise. I absolutely loved it because the energy was incredible!' (last event)",
          a: "Finally",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["At last"] },
          successtip: "✓ FINALLY! Event review complete! Comic-Con! 🎭🦸📦"
        },
        {
          type: "order",
          q: "FINAL CHALLENGE: Complete podcast review: 'Escuché un podcast sobre crímenes reales. Primero describieron el caso. Luego entrevistaron testigos. Finalmente revelaron al culpable. Me encantó porque fue intrigante.'",
          words: ["I", "listened", "to", "a", "podcast", "about", "true", "crime.", "First", "they", "described", "the", "case.", "Then", "they", "interviewed", "witnesses.", "Finally", "they", "revealed", "the", "culprit.", "I", "loved", "it", "because", "it", "was", "intriguing"],
          a: "I listened to a podcast about true crime. First they described the case. Then they interviewed witnesses. Finally they revealed the culprit. I loved it because it was intriguing",
          tip: "Listen→listened (regular)! True crime podcasts = super popular!",
          successtip: "✓ 🎉 PERFECTO! Complete podcast review! About + sequence + opinion! You're a review master! Can describe ANY media now! 🎧🔍❤️"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET - 3 PARAGRAPHS (10 questions)
  // ===================================================================
  // ===================================================================
// READING SET: My Netflix Weekend
// ===================================================================
readingSet: {
  id: "m5-u9-reading",
  title: "Reading: My Netflix Weekend",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Marathon (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Weekend Marathon**

Last weekend I stayed home and had a Netflix marathon. I watched a new series called *The Queen's Gambit*. It's a drama about a young girl named Beth who becomes a chess champion in the 1960s. 

First she learned to play chess in an orphanage. Then she competed in tournaments across the country. Finally she became the best player in the world. The show has seven episodes and I watched them all in two days!`
    },
    {
      type: "mc",
      q: "What is *The Queen's Gambit* about?",
      options: ["A chess champion", "A queen", "A gambler"],
      a: "A chess champion",
      successtip: "✓ Chess champion! Beth's story! ♟️👧"
    },
    {
      type: "input",
      q: "How many episodes did the writer watch in total?",
      a: "seven",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["7", "all seven", "all of them"] },
      successtip: "✓ SEVEN! Complete binge! All episodes! 📺"
    },
    {
      type: "mc",
      q: "How long did it take the writer to finish the series?",
      options: ["Two days", "One weekend", "One week"],
      a: "Two days",
      successtip: "✓ TWO DAYS! Binge-watching! Marathon! 📺⏰"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Why it's Good (preguntas 4-6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Why I Loved It**

I absolutely loved the show! First of all, the acting was brilliant. The main actress, Anya Taylor-Joy, was incredible. She made me believe she was really a chess genius. 

Second, the story was thrilling. I couldn't stop watching because I wanted to know what would happen next. Third, the visuals were stunning. The costumes and sets perfectly captured the 1960s. Every episode looked like a work of art!`
    },
    {
      type: "mc",
      q: "What was the writer's opinion of the acting?",
      options: ["Brilliant", "Boring", "Okay"],
      a: "Brilliant",
      successtip: "✓ BRILLIANT! Anya Taylor-Joy = incredible! 🎭✨"
    },
    {
      type: "input",
      q: "Why couldn't the writer stop watching? The story was ___.",
      a: "thrilling",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["wanted to know what would happen", "the story was thrilling", "it was thrilling"] },
      successtip: "✓ THRILLING! Suspenseful! Wanted to know more! 😰📺"
    },
    {
      type: "mc",
      q: "What historical time period did the show represent?",
      options: ["1960s", "1970s", "1980s"],
      a: "1960s",
      successtip: "✓ 1960s! Costumes and sets! Time period! 🕰️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Sharing the Love (preguntas 7-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Review**

After I finished, I recommended it to all my friends. I posted a review on social media saying: "The Queen's Gambit is a must-watch! It's about chess, but you don't need to know how to play to enjoy it. I loved it because it's inspiring, beautiful, and thrilling. Five stars!" 

Now three of my friends are watching it too. We're planning to have a chess tournament next weekend because the show inspired us!`
    },
    {
      type: "mc",
      q: "Where did the writer share their opinion about the show?",
      options: ["A review on social media", "A video", "Nothing"],
      a: "A review on social media",
      successtip: "✓ Social media review! Sharing opinions! 📱💬"
    },
    {
      type: "input",
      q: "How many stars out of five did the writer give the show?",
      a: "five",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["5", "five stars", "5 stars"] },
      successtip: "✓ FIVE STARS! Perfect score! ⭐⭐⭐⭐⭐"
    },
    {
      type: "mc",
      q: "What activity are the friends planning for next weekend?",
      options: ["To have a chess tournament", "To watch more shows", "To become actors"],
      a: "To have a chess tournament",
      successtip: "✓ Chess tournament! Show inspired real action! ♟️👥"
    },
    {
      type: "mc",
      q: "COMPREHENSION: This text is an example of a:",
      options: ["Netflix review with about, sequence, opinion, and recommendation", "Movie summary", "Chess tutorial"],
      a: "Netflix review with about, sequence, opinion, and recommendation",
      successtip: "✓ Perfect! Complete review structure! About + plot (First/Then/Finally) + opinion (loved because) + social sharing! 🎬❤️📱"
    }
  ]
},
  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m5-u9-review",
    title: "Unit 9 Review: Storytelling Mastery",
    questions: [
      {
        type: "match",
        q: "Review key patterns:",
        pairs: [
          { left: "It's about...", right: "Describes topic" },
          { left: "First, Then, Finally", right: "Sequence events" },
          { left: "I loved it because...", right: "Opinion + reason" }
        ],
        a: "completed",
        successtip: "✓ Three key patterns reviewed! About, Sequence, Because! 📝"
      },
      {
        type: "mc",
        q: "It's a movie ___ superheroes.",
        options: ["about", "because", "then"],
        a: "about",
        successtip: "✓ ABOUT! Describes topic! 🎬"
      },
      {
        type: "input",
        q: "___ I woke up. Then I had breakfast. Finally I went to work. (first word)",
        a: "First",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ FIRST! Sequence starter! ⏰"
      },
      {
        type: "mc",
        q: "I loved the book ___ it was interesting.",
        options: ["because", "about", "then"],
        a: "because",
        successtip: "✓ BECAUSE! Gives reason! 💕➡️📖"
      },
      {
        type: "order",
        q: "Build a review:",
        words: ["It's", "a", "show", "about", "doctors.", "I", "enjoyed", "it", "because", "it", "was", "interesting"],
        a: "It's a show about doctors. I enjoyed it because it was interesting",
        successtip: "✓ Complete review! About + opinion + because! 📺❤️"
      },
      {
        type: "input",
        q: "First I arrived. ___ I found my seat. Finally the movie started. (middle)",
        a: "Then",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Next", "After that"] },
        successtip: "✓ THEN! Middle connector! ➡️"
      },
      {
        type: "mc",
        q: "Who makes movies?",
        options: ["Director", "Author", "Character"],
        a: "Director",
        successtip: "✓ DIRECTOR! Movie maker! 🎬"
      },
      {
        type: "match",
        q: "Review vocabulary:",
        pairs: [
          { left: "Positive: funny, exciting, amazing", right: "😊" },
          { left: "Negative: boring, slow, terrible", right: "😞" },
          { left: "Genres: comedy, drama, horror", right: "🎭" }
        ],
        a: "completed",
        successtip: "✓ Vocabulary reviewed! Adjectives + genres! 📚"
      },
      {
        type: "input",
        q: "I didn't like it because it was ___. (negative adjective)",
        a: "boring",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["slow", "terrible", "bad", "confusing"] },
        successtip: "✓ Negative adjective! Boring/slow/terrible! 😴"
      },
      {
        type: "mc",
        q: "FINAL CHALLENGE: 'I watched Inception. It's a movie about dreams. First the team planned. Then they entered the dream. Finally they completed the mission. I absolutely loved it because it was mind-blowing!' This review has:",
        options: ["About + Sequence (First/Then/Finally) + Opinion (loved) + Reason (because)", "Only opinion", "Only sequence"],
        a: "About + Sequence (First/Then/Finally) + Opinion (loved) + Reason (because)",
        successtip: "✓ 🎉 PERFECTO! Complete review with ALL elements! You can now describe movies, books, shows, and events with about, sequence words, and opinions + reasons! Review master! 🏆🎬📖❤️"
      }
    ]
  }
};
